import { TripLeg } from '../../data/models/reservation.model';
import { SortModel } from '../../data/models/sort-model.model';
import { TripLegEventType } from '@/data/events/trip-leg';
import { FilterModel } from '../../data/models/filters.model';
import { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';

const STRING_COLUMNS = [
  'pickUpDueTimeUTC',
  'appointmentTimeUTC',
  'appointmentDate',
  'tripLegReferenceID',
  'tripLegStatusBC',
  'tripLegStatusName',
  'tripLegExceptionBC',
  'tripLegExceptionName',
  'tripLegExceptionReason',
  'tripLegExceptionReasonName',
  'passengerFirstName',
  'transportationProviderName',
];

const BOOLEAN_COLUMNS = ['isEscalated', 'isUnderDiscussion', 'isException'];

// The grid's column field (used as the sort key in filters.sort) doesn't
// always match the TripLegSearchResponseItem field it actually renders --
// e.g. MonitoringExceptionsTable's Provider column sorts by
// 'transportationProviderName' but the search row stores the value under
// 'providerName'. Map sort keys to their real field name here so
// isTripLegWithinStringColumnSort reads the right property instead of
// silently getting undefined.
const SORT_KEY_FIELD_ALIASES: Partial<
  Record<string, keyof TripLegSearchResponseItem>
> = {
  transportationProviderName: 'providerName',
};

class RealtimeUtils {
  private doesTripLegMatchFilters(
    tripLeg: TripLegSearchResponseItem,
    filters: FilterModel,
  ): boolean {
    if (filters?.appointmentStartDate) {
      if (
        tripLeg.appointmentDate &&
        tripLeg?.appointmentDate.split('T')[0] <
          filters.appointmentStartDate.split('T')[0]
      ) {
        return false;
      }
    }

    if (filters?.appointmentEndDate) {
      if (
        tripLeg.appointmentDate &&
        tripLeg?.appointmentDate.split('T')[0] >
          filters.appointmentEndDate.split('T')[0]
      ) {
        return false;
      }
    }

    // TODO: Determine the actual logic for this filter
    // if (filters?.excludeRideshareWillCalls) {
    //   if (tripLeg.isWillCall) {
    //     return false;
    //   }
    // }

    if (filters?.isException) {
      if (!tripLeg.tripLegExceptionBC) {
        return false;
      }
    }

    if (filters?.isUnderDiscussion) {
      if (!tripLeg.isUnderDiscussion) {
        return false;
      }
    }

    if (filters?.isEscalated) {
      if (!tripLeg.isEscalated) {
        return false;
      }
    }

    if (filters?.isInternal) {
      if (!tripLeg.isInternal) {
        return false;
      }
    }

    if (filters?.tripLegExceptionBC) {
      if (
        tripLeg.tripLegExceptionBC &&
        !filters.tripLegExceptionBC.includes(tripLeg.tripLegExceptionBC)
      ) {
        return false;
      }
    }

    return true;
  }

  private isTripLegWithinStringColumnSort(
    tripLeg: TripLegSearchResponseItem,
    firstTripLeg: TripLegSearchResponseItem,
    lastTripLeg: TripLegSearchResponseItem,
    column: keyof TripLegSearchResponseItem,
    direction: string,
    page: number,
    lastPage: number,
  ): boolean {
    if (
      tripLeg[column] === undefined ||
      firstTripLeg[column] === undefined ||
      lastTripLeg[column] === undefined
    ) {
      return true;
    }

    const isAscending = direction.toLowerCase() === 'asc';
    // In ascending order this page's values increase from firstTripLeg to
    // lastTripLeg; in descending order they decrease. A value that would
    // sort "before" this page's range can only land here if this is the
    // first page (nothing earlier to absorb it instead); a value that
    // would sort "after" this page's range can only land here if this is
    // the last page (it might append here). The two boundaries need
    // separate checks -- collapsing them into a single page === lastPage
    // (or page === 1) check gets one direction right and silently rejects
    // the other.
    const isBeforePage = isAscending
      ? tripLeg[column]! < firstTripLeg[column]!
      : tripLeg[column]! > firstTripLeg[column]!;
    const isAfterPage = isAscending
      ? tripLeg[column]! > lastTripLeg[column]!
      : tripLeg[column]! < lastTripLeg[column]!;

    if (isBeforePage) {
      return page === 1;
    }
    if (isAfterPage) {
      return page === lastPage;
    }
    return true;
  }

  private isTripLegWithinBooleanColumnSort(
    tripLeg: TripLegSearchResponseItem,
    firstTripLeg: TripLegSearchResponseItem,
    lastTripLeg: TripLegSearchResponseItem,
    columns: [keyof TripLegSearchResponseItem, keyof TripLegSearchResponseItem],
    direction: string,
    page: number,
    lastPage: number,
  ): boolean {
    if (
      tripLeg.tripLegExceptionBC &&
      (firstTripLeg[columns[0]] || lastTripLeg[columns[0]])
    ) {
      return this.isTripLegWithinStringColumnSort(
        tripLeg,
        firstTripLeg,
        lastTripLeg,
        columns[1],
        direction,
        page,
        lastPage,
      );
    }
    return false;
  }

  private doesTripLegSortToCurrentPage(
    tripLeg: TripLegSearchResponseItem,
    filters: FilterModel,
    tripLegs: TripLegSearchResponseItem[],
  ): boolean {
    // If it matches all the filters, would it appear on the current page?
    const columns = filters.sort.split(',');
    const directions = filters.sortOrder?.split(',');

    // If there's less trip legs than the page size, we can return early
    if (tripLegs.length < filters.pageSize) {
      return true;
    }

    const firstTripLeg = tripLegs[0];
    const lastTripLeg = tripLegs[tripLegs.length - 1];

    // tripLegs is only the current page's rows (length <= pageSize), so
    // deriving "last page" from its own length is wrong for any page other
    // than the actual last one (it's 1 whenever the current page is full,
    // regardless of how many pages really exist). Use the real item count
    // when the caller has it; only fall back to the current page's own
    // length -- and thus the old, page-1-only-correct behavior -- when it
    // doesn't.
    const lastPage =
      filters.itemCount !== undefined
        ? Math.max(1, Math.ceil(filters.itemCount / filters.pageSize))
        : Math.ceil(tripLegs.length / filters.pageSize);

    // Defaulting to true (rather than false) for a column this function
    // doesn't recognize means an unsupported/single-column sort can't
    // silently block an otherwise-relevant trip leg from refreshing -- it
    // only narrows things down for sorts it can actually evaluate.
    let withinFirstSort = true;
    let withinSecondSort = true;

    if (STRING_COLUMNS.includes(columns?.[0])) {
      const column = (SORT_KEY_FIELD_ALIASES[columns[0]] ??
        columns[0]) as keyof TripLegSearchResponseItem;
      withinFirstSort = this.isTripLegWithinStringColumnSort(
        tripLeg,
        firstTripLeg,
        lastTripLeg,
        column,
        directions?.[0]!,
        filters.page,
        lastPage,
      );
    } else if (BOOLEAN_COLUMNS.includes(columns?.[0])) {
      const columnA = columns?.[0] as keyof TripLegSearchResponseItem;
      const columnB = columns?.[1] as keyof TripLegSearchResponseItem;
      withinFirstSort = this.isTripLegWithinBooleanColumnSort(
        tripLeg,
        firstTripLeg,
        lastTripLeg,
        [columnA, columnB],
        directions?.[0]!,
        filters.page,
        lastPage,
      );
    }

    // No second sort column (a single-column sort) means there's nothing
    // for withinSecondSort to violate, so it stays true.
    if (columns?.[1] && STRING_COLUMNS.includes(columns[1])) {
      const column = (SORT_KEY_FIELD_ALIASES[columns[1]] ??
        columns[1]) as keyof TripLegSearchResponseItem;
      withinSecondSort = this.isTripLegWithinStringColumnSort(
        tripLeg,
        firstTripLeg,
        lastTripLeg,
        column,
        directions?.[1]!,
        filters.page,
        lastPage,
      );
    }

    return withinFirstSort && withinSecondSort;
  }

  public shouldRefreshTripLegData({
    tripLegEventType,
    tripLegEvent,
    tripLegs,
    allTripLegs,
    filters,
  }: {
    tripLegEventType: TripLegEventType;
    tripLegEvent: any;
    tripLegs: TripLegSearchResponseItem[];
    allTripLegs?: TripLegSearchResponseItem[];
    filters?: FilterModel;
  }): boolean {
    // We can't determine sorting logic without the API response filters
    if (!filters || !filters.sortOrder) {
      return false;
    }

    // If the Trip Leg is on the current page, we need to return true.
    const foundIndex = tripLegs.findIndex(
      (tripLeg) =>
        tripLeg.tripLegReferenceID === tripLegEvent.tripLegReferenceID,
    );
    if (foundIndex >= 0) {
      if (tripLegEventType === TripLegEventType.TripLegEscalated) {
        return false;
      }
      return true;
    }

    // Early return here if trip is cancelled. If it wasn't on the current
    // page, we can return false.
    if (
      tripLegEventType === TripLegEventType.TripLegCanceled ||
      tripLegEventType === TripLegEventType.TripLegCanceledLate
    ) {
      return false;
    }

    // From here we might not have all the data in this event, so we need the
    // trip leg from the allTripLegs array.
    const tripLeg = allTripLegs?.find(
      (tripLeg) =>
        tripLeg.tripLegReferenceID === tripLegEvent.tripLegReferenceID,
    );
    // If we don't have the trip leg in the allTripLegs array, we must refetch.
    if (!tripLeg) {
      return true;
    }

    // Does the current trip leg event match all the filters?
    if (!this.doesTripLegMatchFilters(tripLeg, filters)) {
      return false;
    }

    // If the filters include status BCs, we need to check that the detailType
    // matches and then check if the trip leg will appear on the current page.
    if (filters?.tripLegStatusBCs) {
      if (!filters.tripLegStatusBCs.includes(tripLeg.tripLegStatusBC!)) {
        return false;
      } else {
        return this.doesTripLegSortToCurrentPage(tripLeg, filters, tripLegs);
      }
    }

    // We need to check if the trip leg will appear on the current page based on
    // the event type and the sort model. If no status BCs are provided, we can
    // just check the following event types
    if (
      tripLegEventType === TripLegEventType.TripLegOpened ||
      tripLegEventType === TripLegEventType.TripLegUnderDiscussion ||
      tripLegEventType === TripLegEventType.TripLegException ||
      tripLegEventType === TripLegEventType.TripLegEscalated ||
      tripLegEventType === TripLegEventType.WillCallTripLegActivated
    ) {
      return this.doesTripLegSortToCurrentPage(tripLeg, filters, tripLegs);
    }

    return false;
  }
}

const realtimeUtils = new RealtimeUtils();

export default realtimeUtils;
